import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { SelectField, TextAreaField, TextField } from "@/components/base/FormControls";
import { VEHICLE_TYPE_OPTIONS } from "@/lib/constants";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Customer, CustomerVehicle } from "@/types";

interface VehicleFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  vehicle: CustomerVehicle | null;
  /** When provided, the vehicle is fixed to this customer and the owner picker is hidden. */
  customerId?: string | null;
  customers: Customer[];
}

interface VehicleForm {
  customer_id: string;
  brand: string;
  model: string;
  year: string;
  plate: string;
  vin: string;
  color: string;
  vehicle_type: string;
  mileage: string;
  notes: string;
}

const EMPTY: VehicleForm = {
  customer_id: "",
  brand: "",
  model: "",
  year: "",
  plate: "",
  vin: "",
  color: "",
  vehicle_type: "Automóvil",
  mileage: "",
  notes: "",
};

function toInt(value: string): number | null {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function VehicleFormModal({
  open,
  onClose,
  onSaved,
  vehicle,
  customerId = null,
  customers,
}: VehicleFormModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [form, setForm] = useState<VehicleForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (vehicle) {
      setForm({
        customer_id: vehicle.customer_id,
        brand: vehicle.brand ?? "",
        model: vehicle.model ?? "",
        year: vehicle.year != null ? String(vehicle.year) : "",
        plate: vehicle.plate ?? "",
        vin: vehicle.vin ?? "",
        color: vehicle.color ?? "",
        vehicle_type: vehicle.vehicle_type ?? "Automóvil",
        mileage: vehicle.mileage != null ? String(vehicle.mileage) : "",
        notes: vehicle.notes ?? "",
      });
    } else {
      setForm({ ...EMPTY, customer_id: customerId ?? "" });
    }
  }, [open, vehicle, customerId]);

  const update = <K extends keyof VehicleForm>(key: K, value: VehicleForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const ownerId = customerId ?? form.customer_id;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!ownerId) {
      setError("Selecciona el cliente dueño del vehículo.");
      return;
    }
    if (!form.brand.trim() && !form.model.trim() && !form.plate.trim()) {
      setError("Indica al menos marca, modelo o placa del vehículo.");
      return;
    }

    const payload = {
      customer_id: ownerId,
      brand: form.brand.trim() || null,
      model: form.model.trim() || null,
      year: toInt(form.year),
      plate: form.plate.trim().toUpperCase() || null,
      vin: form.vin.trim() || null,
      color: form.color.trim() || null,
      vehicle_type: form.vehicle_type || null,
      mileage: toInt(form.mileage),
      notes: form.notes.trim() || null,
    };

    setSubmitting(true);
    try {
      if (vehicle) {
        const { error: updateError } = await supabase
          .from("customer_vehicles")
          .update(payload)
          .eq("id", vehicle.id);
        if (updateError) {
          setError("No se pudo actualizar el vehículo.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "editar",
          entity: "customer_vehicles",
          entityId: vehicle.id,
          branchId: profile?.branch_id ?? null,
          newValue: { placa: payload.plate, marca: payload.brand, modelo: payload.model },
        });
        toast.success("Vehículo actualizado.");
      } else {
        const { data, error: insertError } = await supabase
          .from("customer_vehicles")
          .insert(payload)
          .select("id")
          .single();
        if (insertError || !data) {
          setError("No se pudo crear el vehículo.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "crear",
          entity: "customer_vehicles",
          entityId: data.id,
          branchId: profile?.branch_id ?? null,
          newValue: { placa: payload.plate, marca: payload.brand, modelo: payload.model },
        });
        toast.success("Vehículo registrado.");
      }
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={vehicle ? "Editar vehículo" : "Nuevo vehículo"}
      subtitle="Los vehículos quedan asociados al cliente para el historial del taller."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="vehicle-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            {vehicle ? "Guardar cambios" : "Registrar vehículo"}
          </button>
        </>
      }
    >
      <form id="vehicle-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        {!customerId ? (
          <SelectField
            label="Cliente dueño"
            name="vehicle_customer"
            value={form.customer_id}
            onChange={(value) => update("customer_id", value)}
            placeholder="Selecciona un cliente"
            required
            options={customers.map((c) => ({
              value: c.id,
              label: `${c.first_name} ${c.last_name}`.trim(),
            }))}
          />
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Marca"
            name="v_brand"
            value={form.brand}
            onChange={(value) => update("brand", value)}
            placeholder="Ej: Toyota"
          />
          <TextField
            label="Modelo"
            name="v_model"
            value={form.model}
            onChange={(value) => update("model", value)}
            placeholder="Ej: Corolla"
          />
          <TextField
            label="Año"
            name="v_year"
            value={form.year}
            onChange={(value) => update("year", value)}
            inputMode="numeric"
            placeholder="Ej: 2018"
          />
          <TextField
            label="Placa"
            name="v_plate"
            value={form.plate}
            onChange={(value) => update("plate", value)}
            placeholder="Ej: A123456"
          />
          <SelectField
            label="Tipo de vehículo"
            name="v_type"
            value={form.vehicle_type}
            onChange={(value) => update("vehicle_type", value)}
            options={VEHICLE_TYPE_OPTIONS}
          />
          <TextField
            label="Color"
            name="v_color"
            value={form.color}
            onChange={(value) => update("color", value)}
            placeholder="Ej: Blanco"
          />
          <TextField
            label="VIN / Chasis"
            name="v_vin"
            value={form.vin}
            onChange={(value) => update("vin", value)}
            placeholder="Número de chasis"
          />
          <TextField
            label="Kilometraje"
            name="v_mileage"
            value={form.mileage}
            onChange={(value) => update("mileage", value)}
            inputMode="numeric"
            placeholder="Ej: 85000"
          />
        </div>

        <TextAreaField
          label="Notas"
          name="v_notes"
          value={form.notes}
          onChange={(value) => update("notes", value)}
          placeholder="Detalles del vehículo, historial o pendientes."
          rows={2}
        />

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}